/**
 * File de synchronisation hors ligne (`ARCHITECTURE_V2.md` §3.3, étape 3).
 *
 * C'est la brique qui rend le hors ligne **utile** plutôt que seulement
 * consultable. L'étape 2 a rendu l'application lisible sans réseau ; celle-ci
 * lui rend le droit d'écrire.
 *
 * 🔴 **La file tourne dans la PAGE, pas dans le service worker.** Background
 * Sync — le réflexe — n'est pas implémenté par Safari iOS, qui est la
 * plateforme cible (§3.3). Conséquence assumée, et qu'il faut **dire à
 * l'utilisateur** plutôt que masquer : une saisie faite hors ligne ne part pas
 * toute seule dans la poche. Elle part à la **réouverture de l'application avec
 * du réseau**. C'est ce que l'écran de détail des Réglages annonce en toutes
 * lettres.
 *
 * Il en découle deux exigences non négociables, et ce fichier n'existe que pour
 * les tenir :
 *
 *   1. la file vit en **IndexedDB** — elle survit à la fermeture de
 *      l'application et au redémarrage de l'appareil ;
 *   2. **rien ne la vide sans accusé de réception du serveur.**
 *
 * 🔴 **Base IndexedDB distincte de `nutri-cache`** (le cache de lecture persisté
 * à l'étape 2). Ce n'est pas du rangement : le cache de lecture est **purgé à la
 * déconnexion**, et une purge qui emporterait des opérations non acquittées
 * détruirait en silence des saisies que l'utilisateur croit enregistrées. Deux
 * bases séparées rendent ce défaut **impossible par construction**, plutôt que
 * dépendant de la discipline de celui qui écrira la prochaine purge.
 *
 * ⚠️ **La file n'est PAS un intercepteur aveugle de tout ce qui n'est pas un
 * `GET`** (§2.4). Une mutation entre dans la file parce qu'elle est inscrite
 * dans `shared/syncable.ts`, jamais par défaut : sans cette liste blanche,
 * « envoyer une notification d'essai » partirait trois heures plus tard sans
 * que personne ne l'ait demandé.
 */

import {
  createdIdSourceOf,
  isSyncable,
  type CreatedIdSource,
  type SyncableMethod,
} from '../../../shared/syncable';
import type { DayDTO } from '../../../shared/types';
import { sendRaw } from '../api/transport';
import { newClientId } from './dates';

/* ==========================================================================
 * Forme d'une opération
 * ======================================================================= */

export type SyncOpStatus = 'pending' | 'inflight' | 'done' | 'failed';

/** Une opération en attente d'accusé de réception (§3.3). */
export interface SyncOp {
  /** Ordre d'émission, strictement croissant — **c'est la clé** (§3.3). */
  seq: number;
  /** UUID ; c'est lui qu'on transporte en `clientId`. */
  opId: string;
  method: SyncableMethod;
  /** Peut porter des place-titres : `/api/meals/{{op:17}}/entries`. */
  path: string;
  /** Peut porter des place-titres, lui aussi. */
  body?: unknown;
  /** `seq` des opérations dont celle-ci attend un `id`. */
  dependsOn: number[];
  status: SyncOpStatus;
  attempts: number;
  lastError?: string;
  createdAt: number;

  /* --- Deux champs au-delà de la forme du §3.3, et pourquoi -------------- */

  /**
   * Identifiant **provisoire** (négatif) sous lequel la ressource créée par
   * cette opération est désignée à l'écran, tant que le serveur n'a pas répondu.
   *
   * 🔴 **Sans lui, composer hors ligne ne survit pas à une fermeture de
   * l'application.** Le §3.3 décrit les place-titres mais pas d'où vient le
   * numéro qu'ils remplacent : à l'écran, un repas créé hors ligne doit bien
   * porter *un* identifiant, sur lequel le bouton « + » de ce repas appellera
   * `POST /api/meals/<id>/entries`. Cette table de correspondance
   * « id provisoire → opération » vit en mémoire ; la persister sur l'opération
   * est ce qui permet de la **reconstituer au redémarrage**. Autrement,
   * rouvrir l'application avant le drainage rendrait le repas inutilisable —
   * visible, mais impossible à remplir.
   */
  provisionalId?: number;
  /**
   * `id` réellement attribué par le serveur, mémorisé **au moment du succès**
   * (§3.3 : « L'`id` renvoyé est mémorisé pour les dépendants »).
   *
   * ⚠️ C'est ce qui oblige à conserver une opération `done` tant qu'une autre
   * en dépend : la purger tout de suite perdrait l'identité que ses dépendants
   * attendent.
   */
  resultId?: number;
}

/** Ce que l'interface a besoin de savoir, sans connaître la mécanique. */
export interface SyncState {
  /** Opérations qui partiront — en attente ou en vol. */
  pending: number;
  /** Opérations en échec définitif, à présenter avec leur motif. */
  failed: SyncOp[];
  /** La file est suspendue en attente d'une reconnexion (401). */
  paused: boolean;
}

/* ==========================================================================
 * Place-titres et identifiants provisoires
 * ======================================================================= */

const PLACEHOLDER = /\{\{op:(\d+)\}\}/g;

function placeholderFor(seq: number): string {
  return `{{op:${String(seq)}}}`;
}

/**
 * Base des identifiants provisoires.
 *
 * 🔴 **Hors de portée de toute valeur métier, et c'est ce qui rend la réécriture
 * du corps sûre.** Un identifiant provisoire est reconnu à sa valeur : si cette
 * valeur pouvait coïncider avec une donnée légitime, on la réécrirait en
 * place-titre et on enverrait n'importe quoi. Le cas n'est pas théorique —
 * `rateKgWeek` d'un objectif de poids est **signé** (étape 8) et vaut très
 * normalement `-1`. Un milliard en dessous, aucune quantité, aucune macro,
 * aucun rythme n'existe, et l'on reste très au-dessus de `Number.MIN_SAFE_INTEGER`.
 *
 * ⚠️ Ce garde-fou ne suffit pas seul : on ne réécrit que les valeurs
 * **effectivement enregistrées** comme provisoires. Les deux conditions
 * ensemble ferment le sujet.
 */
const PROVISIONAL_BASE = -1_000_000_000;

let provisionalCounter = 0;
/** `opId` → identifiant provisoire attribué. Mémoïsé : deux appels, même valeur. */
const provisionalByOp = new Map<string, number>();
/** Identifiant provisoire → `opId`. Reconstituée au démarrage depuis la file. */
const opByProvisional = new Map<number, string>();

/**
 * Identifiant provisoire d'une création, dérivé de son `clientId`.
 *
 * 🔴 **Appelée des deux côtés, et c'est tout l'intérêt.** L'écran s'en sert pour
 * étiqueter le repas fantôme qu'il affiche ; la file s'en sert pour reconnaître
 * ce même repas dans le chemin de l'opération suivante. Comme les deux partent
 * du même `clientId` — celui que l'écran a tiré et qu'il envoie dans le corps —
 * aucun canal n'est nécessaire entre eux.
 *
 * ⚠️ **Un compteur, pas un hachage du `clientId`.** Un hachage serait
 * déterministe sans état, donc séduisant ; il porterait aussi une probabilité
 * de collision non nulle, et une collision ici écrit une entrée dans le mauvais
 * repas. Un compteur est exact. Le prix — une table à reconstituer au
 * redémarrage — est payé par `provisionalId` sur l'opération.
 */
export function provisionalIdFor(opId: string): number {
  const known = provisionalByOp.get(opId);
  if (known !== undefined) return known;

  provisionalCounter += 1;
  const id = PROVISIONAL_BASE - provisionalCounter;
  provisionalByOp.set(opId, id);
  opByProvisional.set(id, opId);
  return id;
}

/** Vrai si cette valeur est un identifiant provisoire, et non une donnée. */
function isProvisional(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value <= PROVISIONAL_BASE;
}

/**
 * Chemin **canonique** d'une requête, pour l'interroger dans la liste blanche.
 *
 * 🔴 **Trouvé au banc, et c'était le défaut qui vidait l'étape de sa
 * substance.** `shared/syncable.ts` contraint ses segments dynamiques —
 * `:id` doit être un entier **positif** — et c'est délibéré : « une liste
 * blanche qui reconnaît une route pour la mauvaise raison finit par en
 * reconnaître une qu'elle devait refuser ».
 *
 * Mais un repas créé hors ligne porte un identifiant **provisoire négatif** :
 * `POST /api/meals/-1000000001/entries` ne ressemblait donc à **aucune** route
 * connue, `isSyncable` répondait `false`, et l'entrée partait au réseau au lieu
 * d'entrer en file. Symptôme observé : les deux entrées loguées dans le repas
 * hors ligne — c'est-à-dire très exactement le critère de l'étape —
 * disparaissaient sans laisser de trace, pendant que les autres passaient.
 *
 * ⚠️ **La bonne correction est ici, pas dans la liste blanche.** La question
 * posée est « **cette route** est-elle éligible ? », et la route est la même que
 * l'identifiant soit déjà attribué ou non. On normalise donc la valeur avant
 * d'interroger, plutôt que de relâcher une contrainte qui protège autre chose.
 */
export function routePathOf(path: string): string {
  return path
    .split('/')
    .map((segment) => {
      // ⚠️ Motif **non global** : `PLACEHOLDER` porte le drapeau `g`, donc son
      // `.test()` est à état et sauterait un segment sur deux.
      if (/^\{\{op:\d+\}\}$/.test(segment)) return '1';
      if (!/^-\d+$/.test(segment)) return segment;
      return isProvisional(Number(segment)) ? '1' : segment;
    })
    .join('/');
}

/* ==========================================================================
 * Stockage
 * ======================================================================= */

/**
 * ⚠️ **Écrit à la main plutôt qu'avec `idb-keyval`**, contrairement au cache de
 * lecture (`lib/persist.ts`). `createStore()` ne sait ouvrir qu'un magasin par
 * base : réutiliser le paquet imposerait soit de partager la base du cache — ce
 * que le §3.3 interdit précisément —, soit d'ouvrir une seconde base pour
 * quelques dizaines de lignes. Le peu de code ci-dessous coûte moins qu'une
 * dépendance de plus, et le projet a déjà tranché ainsi pour les glyphes
 * (étape 5), le graphique (étape 8) et les icônes (étape 9).
 */
const DB_NAME = 'nutri-sync';
const STORE = 'queue';
const KEY = 'ops';

interface Persisted {
  nextSeq: number;
  ops: SyncOp[];
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(STORE);
    };
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => {
      reject(request.error ?? new Error('IndexedDB indisponible.'));
    };
  });
}

async function readPersisted(): Promise<Persisted | null> {
  const db = await openDb();
  try {
    return await new Promise<Persisted | null>((resolve, reject) => {
      const request = db.transaction(STORE, 'readonly').objectStore(STORE).get(KEY);
      request.onsuccess = () => {
        resolve((request.result as Persisted | undefined) ?? null);
      };
      request.onerror = () => {
        reject(request.error ?? new Error('Lecture de la file impossible.'));
      };
    });
  } finally {
    db.close();
  }
}

async function writePersisted(value: Persisted): Promise<void> {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(value, KEY);
      tx.oncomplete = () => {
        resolve();
      };
      tx.onerror = () => {
        reject(tx.error ?? new Error('Écriture de la file impossible.'));
      };
    });
  } finally {
    db.close();
  }
}

/* ==========================================================================
 * État en mémoire
 * ======================================================================= */

let ops: SyncOp[] = [];
let nextSeq = 1;
let ready = false;
let draining = false;
let paused = false;
/** `seq` → instant avant lequel il ne sert à rien de réessayer. En mémoire seulement. */
const retryAt = new Map<number, number>();
let retryTimer: ReturnType<typeof setTimeout> | null = null;

const listeners = new Set<(state: SyncState) => void>();
let onDrained: (() => void) | null = null;

/**
 * ⚠️ **Les écritures sont enchaînées.** Deux saisies rapprochées produiraient
 * sinon deux lectures-modifications-écritures concurrentes de la même clé, dont
 * la seconde écraserait la première : une opération perdue, sans la moindre
 * erreur pour le dire.
 */
let writeChain: Promise<void> = Promise.resolve();

/**
 * ⚠️ **Ne rejette jamais.** Un stockage indisponible — navigation privée, quota
 * atteint, base verrouillée par un autre onglet — dégrade le hors ligne ; il ne
 * doit ni interrompre un drainage en cours ni produire un rejet non traité à
 * chaque saisie. Même discipline que `startPersistence` à l'étape 2 : on
 * signale une fois au journal, et l'application continue.
 */
function save(): Promise<void> {
  const instantane: Persisted = { nextSeq, ops: ops.map((op) => ({ ...op })) };
  const ecrire = () =>
    writePersisted(instantane).catch((cause: unknown) => {
      console.warn('[nutri] File de synchronisation : écriture impossible.', cause);
    });
  writeChain = writeChain.then(ecrire, ecrire);
  return writeChain;
}

/**
 * Instantané courant, **réutilisé tant que rien ne change**.
 *
 * 🔴 **Mémoïsé, et ce n'est pas une optimisation.** `useSyncExternalStore` (côté
 * React, `hooks/useSyncState.ts`) compare l'instantané précédent au suivant par
 * identité : renvoyer un objet neuf à chaque appel lui ferait conclure que
 * l'état change à chaque rendu, et boucler indéfiniment. L'objet n'est donc
 * reconstruit que dans `notify()`, c'est-à-dire quand la file a réellement bougé.
 */
let snapshot: SyncState = { pending: 0, failed: [], paused: false };

export function syncState(): SyncState {
  return snapshot;
}

function notify(): void {
  snapshot = {
    pending: ops.filter((op) => op.status === 'pending' || op.status === 'inflight').length,
    failed: ops.filter((op) => op.status === 'failed').map((op) => ({ ...op })),
    paused,
  };
  for (const listener of listeners) listener(snapshot);
}

/** S'abonner à l'état de la file — la pastille et l'écran de détail en vivent. */
export function subscribeSync(listener: (state: SyncState) => void): () => void {
  listeners.add(listener);
  listener(syncState());
  return () => listeners.delete(listener);
}

/* ==========================================================================
 * Démarrage
 * ======================================================================= */

/**
 * Restaure la file et reconstitue la table des identifiants provisoires.
 *
 * ⚠️ **Ne lève jamais.** Un stockage indisponible (navigation privée, quota,
 * base verrouillée) dégrade le hors ligne ; il ne doit pas empêcher
 * l'application de s'ouvrir. Même règle que `startPersistence` à l'étape 2.
 */
export async function startSyncQueue(): Promise<void> {
  if (ready) return;
  try {
    const stored = await readPersisted();
    if (stored) {
      ops = stored.ops;
      nextSeq = stored.nextSeq;

      /*
       * 🔴 Reconstitution de la table des identifiants provisoires. Sans elle,
       * un repas créé hors ligne puis retrouvé après un redémarrage serait
       * visible mais impossible à remplir : son identifiant à l'écran ne
       * désignerait plus rien.
       */
      for (const op of ops) {
        if (op.provisionalId === undefined) continue;
        provisionalByOp.set(op.opId, op.provisionalId);
        opByProvisional.set(op.provisionalId, op.opId);
        provisionalCounter = Math.max(provisionalCounter, PROVISIONAL_BASE - op.provisionalId);
      }

      // Une opération « en vol » au moment où l'application s'est fermée n'a
      // jamais été acquittée : elle redevient candidate. Le rejeu est sûr —
      // c'est toute la raison d'être de la liste blanche (§2.1).
      for (const op of ops) {
        if (op.status === 'inflight') op.status = 'pending';
      }
    }
  } catch (cause) {
    console.warn('[nutri] File de synchronisation indisponible.', cause);
  }
  ready = true;
  notify();
}

/** Ce que la file fera quand un drainage aura réellement écrit quelque chose. */
export function onSyncDrained(callback: () => void): void {
  onDrained = callback;
}

/* ==========================================================================
 * Mise en file
 * ======================================================================= */

/** La file a-t-elle du travail ? Une nouvelle mutation doit alors passer derrière. */
export function hasQueuedWork(): boolean {
  return ops.some((op) => op.status === 'pending' || op.status === 'inflight');
}

/**
 * Lève quand une référence provisoire ne désigne plus rien.
 *
 * ⚠️ **Une erreur applicative, pas un échec de transport** : la faire passer
 * pour une panne réseau conserverait à l'écran un aperçu que rien ne pourra
 * jamais enregistrer. Ici l'aperçu doit être annulé et le motif affiché.
 */
export class DanglingReferenceError extends Error {
  constructor() {
    super("Cette saisie dépend d’un élément qui n’a pas pu être créé. À refaire.");
    this.name = 'DanglingReferenceError';
  }
}

/** Remplace un identifiant provisoire par un place-titre ou par l'`id` réel. */
function substitute(value: number, dependsOn: Set<number>): number | string {
  const opId = opByProvisional.get(value);
  const owner = opId === undefined ? undefined : ops.find((op) => op.opId === opId);
  if (!owner) throw new DanglingReferenceError();

  // Déjà acquittée : on connaît le vrai `id`, autant l'écrire tout de suite.
  if (owner.status === 'done' && owner.resultId !== undefined) return owner.resultId;
  if (owner.status === 'failed') throw new DanglingReferenceError();

  dependsOn.add(owner.seq);
  return placeholderFor(owner.seq);
}

function rewritePath(path: string, dependsOn: Set<number>): string {
  return path
    .split('/')
    .map((segment) => {
      if (!/^-\d+$/.test(segment)) return segment;
      const value = Number(segment);
      if (!isProvisional(value)) return segment;
      return String(substitute(value, dependsOn));
    })
    .join('/');
}

function rewriteBody(value: unknown, dependsOn: Set<number>): unknown {
  if (isProvisional(value)) return substitute(value, dependsOn);
  if (Array.isArray(value)) return value.map((item) => rewriteBody(item, dependsOn));
  if (typeof value === 'object' && value !== null) {
    const out: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value)) {
      out[key] = rewriteBody(item, dependsOn);
    }
    return out;
  }
  return value;
}

/**
 * Met une mutation en file et renvoie l'opération créée.
 *
 * ⚠️ **L'`opId` reprend le `clientId` du corps quand il y en a un**, plutôt que
 * d'en tirer un nouveau. C'est ce qui fait que l'identité sous laquelle l'écran
 * a affiché sa saisie, celle que le serveur dédoublonne, et celle que la file
 * retrouve dans la réponse sont **une seule et même valeur** — sans quoi
 * retrouver le repas créé dans une journée entière redeviendrait une devinette.
 */
export function enqueue(method: SyncableMethod, path: string, body: unknown): SyncOp {
  const dependsOn = new Set<number>();
  const rewrittenPath = rewritePath(path, dependsOn);
  const rewrittenBody = body === undefined ? undefined : rewriteBody(body, dependsOn);

  const carried =
    typeof rewrittenBody === 'object' && rewrittenBody !== null && 'clientId' in rewrittenBody
      ? (rewrittenBody as { clientId?: unknown }).clientId
      : undefined;
  const opId = typeof carried === 'string' && carried !== '' ? carried : newClientId();

  const op: SyncOp = {
    seq: nextSeq,
    opId,
    method,
    path: rewrittenPath,
    body: rewrittenBody,
    dependsOn: [...dependsOn],
    status: 'pending',
    attempts: 0,
    createdAt: Date.now(),
    ...(provisionalByOp.has(opId) ? { provisionalId: provisionalByOp.get(opId) } : {}),
  };

  nextSeq += 1;
  ops.push(op);
  void save();
  notify();
  return op;
}

/* ==========================================================================
 * Drainage
 * ======================================================================= */

/** Temporisation croissante, plafonnée — 1 s, 2 s, 4 s … 60 s (§3.3). */
function backoffMs(attempts: number): number {
  return Math.min(60_000, 1000 * 2 ** Math.max(0, attempts - 1));
}

/** Résout les place-titres d'une chaîne depuis les `id` déjà connus. */
function resolve(text: string): string {
  return text.replace(PLACEHOLDER, (whole, digits: string) => {
    const seq = Number(digits);
    const source = ops.find((op) => op.seq === seq);
    if (source?.status === 'done' && source.resultId !== undefined) {
      return String(source.resultId);
    }
    // Laissé tel quel : `runnable()` garantit qu'on n'en arrive jamais là.
    return whole;
  });
}

/** Un place-titre occupe **toute** la valeur : `foodId: '{{op:3}}'`. */
const WHOLE_PLACEHOLDER = /^\{\{op:\d+\}\}$/;

/**
 * Résout les place-titres d'un corps.
 *
 * 🔴 **Seule une valeur qui *est* un place-titre redevient un nombre**, et cette
 * précision n'est pas de la pédanterie : un place-titre remplace un `foodId` ou
 * un `sourceMealId`, donc il doit repartir en nombre, sinon zod le refuse. Mais
 * la règle naïve — « toute chaîne de chiffres redevient un nombre » — casserait
 * en silence les champs légitimement textuels et **entièrement numériques** du
 * projet : `barcode` en est un (`'3017620422003'`), et le convertir en nombre
 * enverrait un type que le serveur refuse, sur la voie du scan, sans que rien
 * n'explique pourquoi. On ne convertit donc que ce qu'on a soi-même écrit.
 */
function resolveBody(value: unknown): unknown {
  if (typeof value === 'string') {
    const resolved = resolve(value);
    return WHOLE_PLACEHOLDER.test(value) && /^\d+$/.test(resolved) ? Number(resolved) : resolved;
  }
  if (Array.isArray(value)) return value.map(resolveBody);
  if (typeof value === 'object' && value !== null) {
    const out: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value)) {
      out[key] = resolveBody(item);
    }
    return out;
  }
  return value;
}

/**
 * Abandonne une opération **et tout ce qui en dépend**, en bloc (§3.3).
 *
 * 🔴 « Écrire une entrée dans un repas qui n'a jamais été créé n'a pas de sens ;
 * l'émettre quand même produirait un 404 qu'on ne saurait pas interpréter. »
 * L'abandon est **transitif** : une entrée qui dépend d'un repas abandonné
 * l'est aussi, et ainsi de suite.
 */
function abandonDependents(seq: number, reason: string): void {
  for (const op of ops) {
    if (op.status !== 'pending' || !op.dependsOn.includes(seq)) continue;
    op.status = 'failed';
    op.lastError = reason;
    abandonDependents(op.seq, reason);
  }
}

/** Prochaine opération réellement émettable, ou `null`. */
function runnable(now: number): SyncOp | null {
  for (const op of [...ops].sort((a, b) => a.seq - b.seq)) {
    if (op.status !== 'pending') continue;

    const blocked = op.dependsOn.some((seq) => {
      const source = ops.find((candidate) => candidate.seq === seq);
      return !source || source.status !== 'done' || source.resultId === undefined;
    });
    if (blocked) {
      // Une dépendance définitivement perdue emporte ses dépendants.
      const lost = op.dependsOn.some((seq) => {
        const source = ops.find((candidate) => candidate.seq === seq);
        return !source || source.status === 'failed';
      });
      if (lost) {
        op.status = 'failed';
        op.lastError = 'L’élément dont cette opération dépend n’a pas pu être créé.';
        abandonDependents(op.seq, op.lastError);
      }
      continue;
    }

    const wait = retryAt.get(op.seq);
    if (wait !== undefined && wait > now) continue;
    return op;
  }
  return null;
}

/** Lit l'`id` que le serveur vient d'attribuer, selon le descripteur de la route. */
function readCreatedId(source: CreatedIdSource, payload: unknown, opId: string): number | null {
  if (payload === null || typeof payload !== 'object') return null;

  if (source === 'response-id') {
    const id = (payload as { id?: unknown }).id;
    return typeof id === 'number' ? id : null;
  }

  const day = payload as DayDTO;
  if (!Array.isArray(day.meals)) return null;

  if (source === 'day-meal') {
    return day.meals.find((meal) => meal.clientId === opId)?.id ?? null;
  }

  for (const meal of day.meals) {
    const entry = meal.entries.find((candidate) => candidate.clientId === opId);
    if (entry) return entry.id;
  }
  return null;
}

/** Programme un réveil pour la prochaine opération temporisée. */
function scheduleRetry(): void {
  if (retryTimer !== null) return;
  const now = Date.now();
  const waits = ops
    .filter((op) => op.status === 'pending')
    .map((op) => retryAt.get(op.seq) ?? now)
    .filter((at) => at > now);
  if (waits.length === 0) return;

  const delay = Math.max(250, Math.min(...waits) - now);
  retryTimer = setTimeout(() => {
    retryTimer = null;
    void drainSyncQueue();
  }, delay);
}

/**
 * Vide la file, **une opération en vol à la fois** (§3.3).
 *
 * 🔴 **Strictement séquentiel, et ce n'est pas une simplification.** C'est la
 * seule façon de garantir qu'une dépendance est résolue avant son consommateur :
 * l'entrée ne peut partir qu'une fois le repas créé et son `id` connu. C'est
 * déjà la discipline du projet ailleurs — les appels à Open Food Facts (§5.2) et
 * les envois push (étape 9) sont séquentiels. Le parallélisme ne gagnerait rien
 * de mesurable sur une file qui contient au pire une séance.
 */
export async function drainSyncQueue(): Promise<void> {
  if (!ready || draining || paused) return;
  draining = true;
  let progressed = false;

  try {
    for (;;) {
      const op = runnable(Date.now());
      if (!op) break;

      op.status = 'inflight';
      op.attempts += 1;
      notify();

      const path = resolve(op.path);
      const body = op.body === undefined ? undefined : resolveBody(op.body);
      const outcome = await sendRaw(op.method, path, body);

      if (outcome.kind === 'ok') {
        op.status = 'done';
        op.lastError = undefined;
        const source = createdIdSourceOf(op.method, path);
        if (source) {
          const id = readCreatedId(source, outcome.payload, op.opId);
          if (id !== null) op.resultId = id;
        }
        retryAt.delete(op.seq);
        progressed = true;
        void save();
        notify();
        continue;
      }

      if (outcome.kind === 'network' || outcome.kind === 'abort') {
        /*
         * Le serveur n'a rien répondu : l'opération reste bonne, seul le
         * transport a manqué. On réessaiera, et on **arrête là** — la suite de
         * la file ne partirait pas mieux, et s'obstiner userait la batterie
         * pour rien.
         *
         * ⚠️ Une annulation est traitée comme une panne de transport. La file
         * ne pose aucun `signal`, donc le cas ne se présente pas aujourd'hui ;
         * le traiter explicitement évite qu'un `signal` ajouté un jour ne fasse
         * passer une opération par le chemin des refus définitifs.
         */
        op.status = 'pending';
        op.lastError = outcome.kind === 'network' ? outcome.message : 'Envoi interrompu.';
        retryAt.set(op.seq, Date.now() + backoffMs(op.attempts));
        void save();
        notify();
        break;
      }

      /* --- Le serveur a répondu ---------------------------------------- */

      if (outcome.status === 401) {
        /*
         * 🔴 **Ne surtout pas marquer `failed` : la session a expiré, les
         * opérations sont bonnes** (§3.3). La file s'arrête et attend une
         * reconnexion ; `resumeSyncQueue()` la relance.
         */
        op.status = 'pending';
        op.lastError = 'Session expirée — reconnectez-vous pour reprendre l’envoi.';
        paused = true;
        void save();
        notify();
        break;
      }

      if (outcome.status === 404 && op.method === 'DELETE') {
        // L'état visé est atteint : c'est un succès (convention V1, RFC 9110
        // §9.2.2, en place depuis l'étape 5).
        op.status = 'done';
        op.lastError = undefined;
        progressed = true;
        void save();
        notify();
        continue;
      }

      if (outcome.status === 408 || outcome.status === 429 || outcome.status >= 500) {
        op.status = 'pending';
        op.lastError = outcome.message;
        retryAt.set(op.seq, Date.now() + backoffMs(op.attempts));
        void save();
        notify();
        break;
      }

      /*
       * 4xx restants → échec **définitif**, sans réessai (§3.3) : « une requête
       * refusée pour sa forme le sera autant demain ». L'opération est
       * présentée à l'utilisateur, pas retentée en boucle — et ses dépendantes
       * sont abandonnées avec elle.
       */
      op.status = 'failed';
      op.lastError = outcome.message;
      abandonDependents(op.seq, 'L’élément dont cette opération dépend a été refusé.');
      void save();
      notify();
    }
  } finally {
    draining = false;
    purgeSettled();
    await save();
    notify();
    scheduleRetry();
  }

  if (progressed && onDrained) onDrained();
}

/**
 * Retire les opérations acquittées dont plus personne ne dépend.
 *
 * ⚠️ **Une opération `failed` n'est JAMAIS retirée ici** (§3.3) : « sans quoi
 * une séance disparaîtrait sans que rien ne le dise, ce qui est exactement le
 * défaut que le hors ligne existe pour empêcher ». Elle reste visible avec son
 * motif jusqu'à ce que l'utilisateur en décide.
 */
function purgeSettled(): void {
  const needed = new Set<number>();
  for (const op of ops) {
    if (op.status === 'done') continue;
    for (const seq of op.dependsOn) needed.add(seq);
  }

  const survivors = ops.filter((op) => op.status !== 'done' || needed.has(op.seq));
  const removed = ops.filter((op) => !survivors.includes(op));
  for (const op of removed) {
    if (op.provisionalId !== undefined) {
      opByProvisional.delete(op.provisionalId);
      provisionalByOp.delete(op.opId);
    }
    retryAt.delete(op.seq);
  }
  ops = survivors;
}

/* ==========================================================================
 * Reprise, abandon, déclencheurs
 * ======================================================================= */

/** Relance une file suspendue par un 401, une fois la session rouverte. */
export function resumeSyncQueue(): void {
  if (!paused) return;
  paused = false;
  notify();
  void drainSyncQueue();
}

/** Remet une opération en échec dans la file — geste explicite de l'utilisateur. */
export function retryFailed(seq: number): void {
  const op = ops.find((candidate) => candidate.seq === seq);
  if (!op || op.status !== 'failed') return;
  op.status = 'pending';
  op.attempts = 0;
  op.lastError = undefined;
  retryAt.delete(seq);
  void save();
  notify();
  void drainSyncQueue();
}

/**
 * Abandonne définitivement une opération en échec — **et ses dépendantes**.
 *
 * ⚠️ Le seul chemin par lequel une opération quitte la file sans avoir été
 * acquittée, et il passe par un geste de l'utilisateur qui a lu le motif.
 */
export function discardFailed(seq: number): void {
  const op = ops.find((candidate) => candidate.seq === seq);
  if (!op) return;
  abandonDependents(seq, 'Abandonnée avec l’opération dont elle dépendait.');
  const doomed = new Set<number>([seq]);
  for (;;) {
    const before = doomed.size;
    for (const candidate of ops) {
      if (candidate.dependsOn.some((dep) => doomed.has(dep))) doomed.add(candidate.seq);
    }
    if (doomed.size === before) break;
  }
  ops = ops.filter((candidate) => !doomed.has(candidate.seq));
  void save();
  notify();
}

/**
 * Branche les déclencheurs de drainage (§3.3).
 *
 * ⚠️ **`visibilitychange` est le déclencheur qui compte sur iPhone.** Une PWA
 * qu'on rouvre ne recharge pas la page : sans lui, une file constituée en salle
 * attendrait le prochain vrai démarrage de l'application.
 */
export function watchSyncTriggers(): () => void {
  const wake = () => {
    void drainSyncQueue();
  };
  const onVisible = () => {
    if (document.visibilityState === 'visible') wake();
  };

  window.addEventListener('online', wake);
  document.addEventListener('visibilitychange', onVisible);

  return () => {
    window.removeEventListener('online', wake);
    document.removeEventListener('visibilitychange', onVisible);
  };
}

/**
 * Une requête vient d'aboutir : le réseau est manifestement revenu (§3.3).
 *
 * ⚠️ Appelée par `apiFetch` sur **tout** succès, y compris une lecture. C'est le
 * déclencheur le plus fiable des quatre : `navigator.onLine` ment (wifi captif,
 * tunnel), une réponse du serveur non.
 */
export function noteNetworkAlive(): void {
  if (!hasQueuedWork()) return;
  /*
   * 🔴 **C'est aussi ici que se lève une suspension pour 401.** Une requête qui
   * aboutit prouve deux choses d'un coup : le réseau répond, **et** la session
   * est valide. Guetter la reconnexion autrement — s'abonner à l'état
   * d'authentification, écouter l'écran de connexion — demanderait à la file de
   * connaître l'authentification, alors qu'une réponse réussie le dit déjà.
   */
  paused = false;
  void drainSyncQueue();
}

/** Cette mutation doit-elle passer par la file ? — liste blanche du §2.4. */
export function isQueueable(method: string, path: string): boolean {
  return isSyncable(method, path);
}
